import { inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { CanActivateFn, Router } from '@angular/router';

/**
 * Qui peut ouvrir /admin-dev.
 *
 * Une seule ligne à éditer pour ouvrir la console à quelqu'un d'autre. C'est
 * volontairement une constante compilée et pas un document Firestore : la
 * console est un outil personnel en construction, et un document modifiable à
 * chaud serait une surface d'élévation de privilège pour un gain nul.
 */
export const ADMIN_DEV_ALLOWLIST: readonly string[] = ['martin@proporio.com'];

/**
 * Normalise une adresse avant comparaison.
 *
 * Firebase conserve la casse saisie à l'inscription ; une session ouverte par
 * lien de connexion peut rendre `Martin@Proporio.com`. Comparer brut ferait
 * refuser le seul compte autorisé, et le refus étant silencieux (voir plus
 * bas), personne ne comprendrait pourquoi.
 */
function normalise(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

/**
 * Garde de /admin-dev — deux conditions, pas une.
 *
 * L'ALLOWLIST NE SUFFIT PAS, et ce n'est pas de la ceinture-et-bretelles.
 * `firestore.rules` n'accorde l'accès aux collections d'administration qu'au
 * claim `superadmin` (`isSuperAdmin()`, firestore.rules:17-21). Un compte
 * allowlisté mais sans le claim ouvrirait une console dont chaque lecture
 * échoue — un écran qui donne l'illusion de fonctionner est pire qu'un refus.
 * Le garde refuse donc dans le même sens que le serveur.
 *
 * LE JETON EST RELU À CHAQUE PASSAGE. La propagation d'un claim personnalisé
 * prend une trentaine de secondes ; un jeton en cache peut donc affirmer le
 * contraire de la vérité juste après un changement de rôle. Même raisonnement
 * que `admin/guards/admin-role.guard.ts`.
 *
 * LE REFUS EST SILENCIEUX. Toute issue autre que « autorisé » renvoie vers
 * /admin, sans message : pendant la construction, rien ne doit signaler
 * l'existence de la console à un autre administrateur. La contrepartie assumée
 * est qu'un refus ne s'explique pas, y compris pour soi : si /admin-dev renvoie
 * vers /admin alors que l'adresse est la bonne, c'est le claim `superadmin` qui
 * manque sur le jeton.
 *
 * AUCUNE CONDITION D'ENVIRONNEMENT ICI. La console existe sur les deux
 * domaines : dev.propor.io lit les données de dev, app.propor.io les vraies.
 * C'est le verrou d'écriture (`admin-dev-env.ts`) qui distingue la production,
 * pas ce garde — bloquer la production rendrait l'outil inutile, puisque les
 * problèmes qu'on veut inspecter y vivent.
 */
export const adminDevGuard: CanActivateFn = async () => {
  const auth = inject(Auth);
  const router = inject(Router);

  await auth.authStateReady();
  const user = auth.currentUser;

  if (!user) {
    return router.parseUrl('/auth/login');
  }

  if (!ADMIN_DEV_ALLOWLIST.includes(normalise(user.email))) {
    return router.parseUrl('/admin');
  }

  // `true` force le rafraîchissement : sans lui, le jeton est celui du cache
  // (valable jusqu'à une heure) et ne porterait pas un claim posé depuis.
  const token = await user.getIdTokenResult(true);
  // Seul `roles` (tableau) est lu par firestore.rules (isSuperAdmin) : accepter
  // aussi le scalaire hérité `role` ouvrirait une console dont chaque lecture
  // échoue.
  const claims = token.claims as { roles?: unknown };
  const roles = new Set<unknown>(Array.isArray(claims.roles) ? claims.roles : []);

  if (!roles.has('superadmin')) {
    return router.parseUrl('/admin');
  }

  return true;
};
